import { CdkStep, CdkStepper, StepperSelectionEvent } from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Validators, ReactiveFormsModule, NonNullableFormBuilder } from '@angular/forms';
import { GoGreenStatusResponse } from '@api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Intention } from '../../models/intention';
import { VerificationStatus } from '../../models/verificaion-status';
import { ServerEmailValidator } from '../../validators/email.validator';
import { NgClass } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatStepperModule } from '@angular/material/stepper';

@Component({
  selector: 'app-go-green-opt-in',
  template: `
    <mat-stepper orientation="vertical" [linear]="true" (selectionChange)="onSelectionChange($event)">
      <mat-step>
        <ng-template matStepLabel>Confirm Email Address</ng-template>
        <ng-template matStepContent>
          <ng-container>
            <p id="email-correct-radio-group-label">
              We have your email address as {{ info.email_address }}. Electronic notification of documents will be sent to this address.
            </p>
            <mat-radio-group
              aria-labelledby="email-correct-radio-group-label"
              class="vertical-radio-group"
              (change)="submitCorrectDetailsChanged.emit($event.value)"
            >
              <mat-radio-button [value]="false"> Yes, the details mentioned above are correct </mat-radio-button>
              <mat-radio-button [value]="true"> No, I want to submit correct details </mat-radio-button>
            </mat-radio-group>
          </ng-container>

          <button class="btn btn-primary" data-testid="step1-next" matStepperNext>Next</button>
        </ng-template>
      </mat-step>
      @if (intention === 'withCorrectedDetails' || intention === 'needsVerification') {
        <mat-step #verificationStep [completed]="verificationStatus === 'changed'">
          @if (intention === 'withCorrectedDetails') {
            <ng-template matStepLabel>Change Email Address</ng-template>
          }
          @if (intention === 'needsVerification') {
            <ng-template matStepLabel>Validate Email Address</ng-template>
          }
          <ng-template matStepContent>
            @if (verificationStatus === 'input') {
              <div
                class="form-group"
                [formGroup]="verificationForm"
                [ngClass]="{
                  'has-success': verificationForm.controls.emailAddress.valid,
                  'has-error': verificationForm.controls.emailAddress.invalid,
                }"
              >
                <label>Email Address</label>
                <input type="text" class="form-control" formControlName="emailAddress" />
                @if (verificationForm.controls.emailAddress.errors?.email) {
                  <span class="help-block">Invalid email address</span>
                }
                @if (verificationForm.controls.emailAddress.errors?.required) {
                  <span class="help-block">Required</span>
                }
              </div>
              <button
                class="btn btn-primary"
                [disabled]="verificationForm.invalid || verificationForm.status === 'PENDING'"
                (click)="onSendVerificationEmailClick()"
              >
                Send Verification Email
              </button>
            }
            @if (verificationStatus === 'waiting') {
              <div>
                <p>
                  A verification link has been sent to your new email address. Please click that link and return to this page to continue.
                  If you can't find the email please check your Junk or Spam folder.
                </p>
                <button class="btn btn-default" (click)="cancelVerify()">Cancel</button>
              </div>
            }
            @if (verificationStatus === 'changed' && intention === 'withCorrectedDetails') {
              <div>
                <p>Your email address has been changed.</p>
                <button class="btn btn-primary" matStepperNext data-testid="verify-next">Next</button>
              </div>
            }
            @if (verificationStatus === 'changed' && intention === 'needsVerification') {
              <div>
                <p>Your email address has been verified.</p>
                <button class="btn" matStepperNext data-testid="verify-next">Next</button>
              </div>
            }
          </ng-template>
        </mat-step>
      }
      <mat-step>
        <ng-template matStepLabel>Review Disclosures</ng-template>
        <ng-template matStepContent>
          <div class="disclosures" [innerHtml]="info.disclosures"></div>
          <button class="btn btn-default" matStepperPrevious>Back</button>
          <button class="btn btn-primary" style="margin-left: 1em;" matStepperNext data-testid="disclosures-next" (click)="onComplete()">
            Complete
          </button>
        </ng-template>
      </mat-step>
    </mat-stepper>
  `,
  styles: [
    `
      .disclosures {
        overflow-y: scroll;
        max-height: 300px;
        margin-top: 10px;
        margin-bottom: 10px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatStepperModule, MatRadioModule, ReactiveFormsModule, NgClass],
})
export class GoGreenOptInComponent implements OnInit, OnChanges, OnDestroy {
  private serverEmailValidator = inject(ServerEmailValidator);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(NonNullableFormBuilder);

  @Input() readonly info: GoGreenStatusResponse;
  @Input() readonly intention: Intention;
  @Input() readonly verificationStatus: VerificationStatus;

  @Output() sendVerificationEmail = new EventEmitter<{
    emailAddress: string;
  }>();
  @Output() optIn = new EventEmitter<{ email: string }>();
  @Output() cancelVerification = new EventEmitter<void>();
  @Output() submitCorrectDetailsChanged = new EventEmitter<boolean>();

  @ViewChild(CdkStepper, { static: true }) stepper: CdkStepper;
  @ViewChild('verificationStep') verificationStep: CdkStep | undefined;

  private destroyed$ = new Subject<void>();

  verificationForm = this.fb.group({
    emailAddress: this.fb.control('', [Validators.required], [(control) => this.serverEmailValidator.validate(control)]),
  });

  ngOnInit() {
    this.verificationForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.verificationStatus?.previousValue === 'waiting' &&
      changes.verificationStatus?.currentValue === 'input' &&
      this.intention === 'needsVerification'
    ) {
      this.stepper.selectedIndex = 0;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectionChange(event: StepperSelectionEvent) {
    if (event.selectedStep === this.verificationStep && this.verificationStatus === 'input' && this.intention === 'needsVerification') {
      this.sendVerificationEmail.emit({
        emailAddress: this.info.email_address,
      });
    }
  }

  onSendVerificationEmailClick() {
    this.sendVerificationEmail.emit({
      emailAddress: this.verificationForm.getRawValue().emailAddress,
    });
  }

  cancelVerify() {
    this.cancelVerification.emit();
  }

  onComplete() {
    const formValue = this.verificationForm.getRawValue();

    this.optIn.emit({
      email: this.intention === 'withCorrectedDetails' ? formValue.emailAddress : this.info.email_address,
    });
  }
}
